import { Injectable } from "@angular/core";

import { NgbModal } from "@ng-bootstrap/ng-bootstrap";

import { Menu, Status, StatusChange, StatusChangeHistory, User } from "@common/models";

import {
  ApprovalListModalComponent,
  ReportViewerModalComponent,
  StatusCommentsComponent,
} from "app/components";
import { TranslateService } from "@ngx-translate/core";
import { getTranslateValue } from "@core/utils";
import { CustomSwalService } from ".";
import { ProdFormula } from "@ryd/models";
import { NotificationService } from "@bpm/services/notification.service";
import { ApprovalFlowComponent } from "app/components/approval-flow/approval-flow.component";
import { Subscription } from "rxjs";
import { StatusChangeHistoryService, StatusChangeService } from "@common/services";
import { InsightsService } from "@core/services";
import { environment } from "environments/environment";
import { NgBlockUI } from "ng-block-ui";
import { ProdFormulaService } from "@ryd/services";
import { QueueService } from "@bpm/services/queue.service";

@Injectable({ providedIn: "root" })
export class StatusChangeModalService {

  private subscriptions = new Subscription();

  constructor(private _modalService: NgbModal, private _traslateService: TranslateService,
    private _swalService: CustomSwalService,
    private _notificacionService: NotificationService,
    private _statusChangeService: StatusChangeService,
    private _log: InsightsService,
    private _statusChangeHistoryService: StatusChangeHistoryService,
    private _prodFormulaService: ProdFormulaService,
    private _queueService: QueueService) { }

  /**
   * Función para abrir el modal para cambio de estado
   * @param {StatusChange} statusChange Objeto de tipo cambio de estado
   * @param {any} obj Objeto de cualquier tipo
   * @param {any} event Asignación de la función a ejecutar
   * @param {string} fieldName Indica el nombre del campo para nombre
   * @param {string} fieldId Indica el nombre del campo para id del objeto
   */
  modalChangeStatus(
    statusChange: StatusChange,
    obj: any,
    event: any,
    fieldName: string = "name",
    fieldId: string = "id"
  ) {
    const statusCommentsModal = this._modalService.open(
      StatusCommentsComponent,
      {
        backdrop: "static",
        size: "lg",
        centered: true,
        animation: true,
        windowClass: "status-comments-modal",
        keyboard: false,
      }
    );
    //get current lang
    let lang = this._traslateService.currentLang;
    let data: any;
    data = {
      obj: statusChange,
      property: "title"
    };
    //Get status traslate
    let status = getTranslateValue(statusChange.title, data, lang);

    statusCommentsModal.componentInstance.title = `${status} - ${obj[fieldName]}`;
    statusCommentsModal.componentInstance.statusChange = statusChange;
    statusCommentsModal.componentInstance.objectId = obj[fieldId];
    statusCommentsModal.componentInstance.objectName = obj[fieldName];
    statusCommentsModal.componentInstance.approvalEmitter.emit = event;
  }

  /**
   * Función para abrir el modal de tipo aprobaciones
   * @param {StatusChange} statusChange Objeto de tipo cambio de estado
   * @param {any} obj Objeto de cualquier tipo
   * @param {string} fieldId Indica el nombre del campo para id
   * @param {string} fieldName Indica el nombre del campo para nombre
   */
  modalApprovalList(
    statusChange: StatusChange,
    obj: any,
    fieldId: string = "id",
    fieldName: string = "name"
  ) {
    const approvalListModal = this._modalService.open(ApprovalListModalComponent, {
      backdrop: "static",
      size: "lg",
      centered: true,
      animation: true,
      windowClass: "approval-list-modal",
      keyboard: false,
    });

    //get current lang
    let lang = this._traslateService.currentLang;
    let data: any;
    data = {
      obj: statusChange,
      property: "title"
    };
    //Get status traslate
    let status = getTranslateValue(statusChange.title, data, lang);

    approvalListModal.componentInstance.title = `${status} - ${obj[fieldName]}`;
    approvalListModal.componentInstance.menuId = statusChange.menu.id;
    approvalListModal.componentInstance.objectId = obj[fieldId];
    approvalListModal.componentInstance.statusChange = statusChange;
  }

  /**
   * Función para abrir el modal de reporte
   * @param {StatusChange} statusChange Objeto de tipo cambio de estado
   * @param {any} obj Objeto de cualquier tipo
   * @param {string} fieldId Indica el nombre del campo para id
   * @param {string} fieldName Indica el nombre del campo para nombre
   */
  modalReport(
    statusChange: StatusChange,
    obj: any,
    fieldId: string = "id",
    fieldName: string = "name"
  ) {

    const objectIds: any[] = [obj[fieldId]];

    // lg y no xl: el tema corre .modal-xl a la izquierda; el modal sube su max-width (ver CLAUDE.md)
    const reportModal = this._modalService.open(ReportViewerModalComponent, {
      backdrop: "static",
      size: "lg",
      centered: true,
      animation: true,
      windowClass: "report-viewer-modal",
      keyboard: false,
    });


    //get current lang
    let lang = this._traslateService.currentLang;
    let data: any;
    data = {
      obj: statusChange,
      property: "title"
    };
    //Get status traslate
    let status = getTranslateValue(statusChange.title, data, lang);

    reportModal.componentInstance.title = `${status} - ${obj[fieldName]}`;
    reportModal.componentInstance.menuId = statusChange.permission.id;
    reportModal.componentInstance.objectIds = objectIds;

  }


  /**
   * Valida y lanza modal de cambio de estado para ProdFormula
   * @param formula La fórmula a cambiar de estado
   * @param statusChange El estado a cambiar
   */
  async changeFormulaStatus(formula: ProdFormula, statusChange: StatusChange, currentUser: User, menu: Menu, activeStatus: Status, sectionBlock: NgBlockUI): Promise<boolean> {

    return new Promise<boolean>(async (resolve) => {

      // Trace Log
      this.createLog(`Se inicia operación ${statusChange.statusChangeType.name} para fórmula ${formula?.code}`, formula, false, currentUser);

      // Indicativo de carga
      //this._swalService.showLoading();
      let msj = "";

      // Consulta permisos para el objeto y estado para el usuario
      const permitions = await this._notificacionService.getByObjectIdAndUsersIdAndNewStatusCode(formula.id, currentUser.id, statusChange.newStatus.code).toPromise();

      // Actions due permitions
      if (!permitions) {

        const notificacions = await this._notificacionService.getByObjectIdAndMenuId(formula.id, menu.id).toPromise();

        if (notificacions?.length > 0) {

          Promise.resolve().then(() => {
            this._traslateService.get(["FORMULAS.DIALOG.ADVICE_USER_NOT_IN_APPROBAL_FLOW"]).subscribe((value) => {
              this._swalService.showWarningMsg(value["FORMULAS.DIALOG.ADVICE_USER_NOT_IN_APPROBAL_FLOW"]);
            });
          });

          // TODO
          resolve(false);

        } else {

          this._swalService.close();
          this.approvalFlowOpen(formula, notificacions, menu.id);

          // TODO
          resolve(false);
        }

      } else {

        // Validating Params
        if (formula.prodFormulaDetails.length == 0) {

          const params = this._traslateService.get(["FORMULAS.DIALOG.PARAMS"]).subscribe((value) => {
            msj += value["FORMULAS.DIALOG.PARAMS"];
          });
          this.subscriptions.add(params);

          resolve(false);
        }

        // Validating alternative
        if (formula.prodCompositions.length == 0) {
          const alt = this._traslateService.get(["FORMULAS.DIALOG.ALTERNATIVE", "FORMULAS.DIALOG.ALTERNATIVE_AND",]).subscribe((value) => {
            msj += msj.length > 0 ? value["FORMULAS.DIALOG.ALTERNATIVE_AND"] : value["FORMULAS.DIALOG.ALTERNATIVE"];
          });
          this.subscriptions.add(alt);
          resolve(false);
        }

        if (msj.length > 0) {
          const msj_sub = this._traslateService.get(["FORMULAS.DIALOG.FORMULA_HAS_NOT"]).subscribe((value) => {
            const msj_sub = this._swalService.showWarningMsg(value["FORMULAS.DIALOG.FORMULA_HAS_NOT"] + msj);
          });
          this.subscriptions.add(msj_sub);
          resolve(false);
        }

        // User not in a group
        if (!this._statusChangeService.validateUserAndGroup(statusChange, activeStatus)) {

          Promise.resolve().then(() => {
            this._traslateService.get(["FORMULAS.DIALOG.ADVICE_USER_NOT_IN_GROUP"]).subscribe((value) => {
              this._swalService.showWarningMsg(value["FORMULAS.DIALOG.ADVICE_USER_NOT_IN_GROUP"]);
            });
          });
          resolve(false);
        }

        //Trace log
        this.createLog(`Cambio de estado para fórmula ${formula.code}`, formula, false, currentUser);

        //Handler para el modal a abrir por el servicio
        const statusChangeHandler = async ({ comment, isApproved, commentEnglish, }: { comment: string; isApproved: boolean; commentEnglish?: string; }) => {
          this._swalService.showLoading();

          if (!isApproved) {

            sectionBlock.start();

            await this.createStatusChangeHistory(statusChange, formula, comment, isApproved, menu, activeStatus, currentUser, commentEnglish);

            this.createLog(`La fórmula ${formula.code} ha sido rechazada debido a: ${comment}`, formula, false, currentUser);
            await this.sendMessageToQueue(menu, formula, false, currentUser);

            const value = await this._traslateService.get(["FORMULAS.DIALOG.HISTORY_SUCCESS"]).toPromise();
            this._swalService.showSuccessMsg(value['FORMULAS.DIALOG.HISTORY_SUCCESS']).then((i) => {
              sectionBlock.stop();
              resolve(true);
            });

            //Cargar de nuevo las fórmulas
            //this.setPage({ offset: 0 });

          } else {
            //Obtener historial de cambio de estado de esta fórmula
            try {
              const res = await this._statusChangeHistoryService.getByMenuIdAndObjectId(menu.id, formula.id).toPromise();

              // Se obtiene el número de aprobaciones validas para el nuevo estado
              const aprob = res.filter((sCH) => sCH.isValid
                && sCH.newStatus.id === statusChange.newStatus.id
                && sCH.status.id === activeStatus.id);

              //Si ha llegado al número de aprobadores cambiar de estado
              if (aprob.length + 1 >= statusChange.approvers) {
                //Generar cambio de estado
                sectionBlock.start();
                const { newStatus } = statusChange;

                const formulaStatusChanged = await this._prodFormulaService.changeFormulaStatus(formula.id, newStatus.id).toPromise();

                //Get the current LANG
                let lang = this._traslateService.currentLang;
                let obj: any;
                obj = {
                  obj: newStatus,
                  property: "name",
                };
                //send the status to translate.
                let status = getTranslateValue(newStatus.name, obj, lang);

                //send the messageToQueue
                await this.sendMessageToQueue(menu, formulaStatusChanged.formula, true, currentUser);

                const value = await this._traslateService.get("FORMULAS.DIALOG.CHANGE_STATUS_SUCCESS", { formula_code: formula.code, status: status, }).toPromise();

                this._swalService.close();
                await this._swalService.showSuccessMsg(value);

                //Si cambia a aprobado, crear un control
                if (formulaStatusChanged.formula) {
                  await this.createStatusChangeHistory(statusChange, formula, comment, isApproved, menu, activeStatus, currentUser, commentEnglish);
                  this.createLog(`Se cambio de estado a la formula ${formula.code}`, formula, false, currentUser);
                  sectionBlock.stop();
                  resolve(true);
                }

              } else {

                // crea registro de historial
                await this.createStatusChangeHistory(statusChange, formula, comment, isApproved, menu, activeStatus, currentUser, commentEnglish);
                this.createLog(`Se realizó una aprobación de fórmulas para el estado ${formula.code}`, formula, false, currentUser);

                const value = await this._traslateService.get(["FORMULAS.DIALOG.HISTORY_SUCCESS"]).toPromise();
                await this._swalService.showSuccessMsg(value['FORMULAS.DIALOG.HISTORY_SUCCESS']);
                sectionBlock.stop();
                resolve(true);

              }
            } catch (err) {
              Promise.resolve().then(() => {
                this._traslateService.get(["FORMULAS.DIALOG.ERROR_SUM_RECORDS", "COMMON.ACTIONS.OOPS"]).subscribe((value) => {
                  this._swalService.showErrorMsg(value["FORMULAS.DIALOG.ERROR_SUM_RECORDS"], value["COMMON.ACTIONS.OOPS"]);
                });
              });
              resolve(false);
            }
          }
        };

        this.modalChangeStatus(statusChange, formula, statusChangeHandler, formula.code ? "code" : (this._traslateService.currentLang == "en" ? "productNameEnglish" : "productName"));

      } // permission

      //resolve(false);

    }); // promise

  }


  /**
   * Creates formula change state history record
   * @param statusChange new statucChange
   * @param formula formula
   * @param comment status change description
   * @param isApproved is aprooved
   * @param menu menu
   * @param activeStatus active status
   * @param currentUser user 
   * @param commentEnglish status change description
   */
  async createStatusChangeHistory(statusChange: StatusChange, formula: ProdFormula, comment: string, isApproved: boolean, menu: Menu, activeStatus: Status, currentUser: User, commentEnglish?: string) {
    //Crear historial de cambios
    const { oldStatus, newStatus } = statusChange;

    const newHistory: StatusChangeHistory = {
      menu: menu,
      objectId: formula.id,
      oldStatus,
      newStatus,
      comment,
      isValid: isApproved,
      commentEnglish,
    };
 
    this._swalService.showLoading();

    await this._statusChangeHistoryService.addStatusChangeHistory(newHistory, false).toPromise();

    // Se llama a endpoint para enviar notificación por correo a todos los aprobadores
    if (newStatus.code == "APR") {
      
      const statusChangeHistory = await this._statusChangeHistoryService.getByMenuIdAndObjectId(menu.id, formula.id).toPromise()

      // Buscando historial de cambios de estado
      const aprobaciones = statusChangeHistory.filter(sCH => sCH.isValid
        && sCH.newStatus.id === newStatus.id
        && sCH.status.id === activeStatus.id
      );

      // validando numero de aprobadores
      if (aprobaciones.length >= statusChange.approvers) {

        const approvsub = this._prodFormulaService.sendApproversNotification(formula).toPromise();

        this.createLog(`Se envió notificación a aprobadores para formula ${formula.code}`, formula, false, currentUser);

      }
    }

  }

  /**
   * Función para enviar mensaje a cola
   * @param object mensaje
   * @param status si es false es un rechazo
   */
  async sendMessageToQueue(menu: Menu, object: any, status: boolean, currentUser: User) {

    try {
      let message = {
        objectId: object.id,
        menuId: menu.id,
        oldStatusId: object.status.id,
        status: status,
      };

      await this._queueService.sendMessage(message, "notifications").toPromise();

    } catch (error) {
      // Manejar la excepción aquí si es necesario
      this.createLog("No se pudo enviar a la cola de notificaciones.", object, true, currentUser, error);
    }
  }


  /**
   * Abre el modal de flujo de aprobación
   * @param object 
   * @param notification 
   * @param menuId 
   */
  private approvalFlowOpen(object, notification, menuId) {
    const approvalListModal = this._modalService.open(
      ApprovalFlowComponent,
      {
        backdrop: "static",
        size: "lg",
        centered: true,
        animation: true,
        windowClass: "approval-flow-modal",
        keyboard: false,
      }
    );
    approvalListModal.componentInstance.object = object;
    approvalListModal.componentInstance.menuId = menuId;
    approvalListModal.componentInstance.canClose = true;
    approvalListModal.componentInstance.userSelectedInput = notification;

  }

  /**
   * Shortcut to create log
   * @param log Message
   * @param obj object 
   */
  private createLog(log: string, obj: any, isError: boolean, currentUser: User, err?: string) {

    isError ? this._log.traceError(environment.projects.Formulas, log, obj, err, currentUser) : this._log.traceInfo(environment.projects.Formulas, log, obj, currentUser);

  }

  /**
   * Manually unsubscribe from subscription
   */
  unsubscribe() {

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

  }

}
